import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Modal as LibModal,
  ModalHeader as LibModalHeader,
  ModalContent as LibModalContent,
  ModalFooter as LibModalFooter
} from 'qampo-ui-library';
import { useScrollLock } from '../../hooks/useScrollLock';

let openModalCount = 0;

const surfaceBg: React.CSSProperties = {
  background: 'var(--color-surface-primary)'
};

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  onConfirm?: () => void;
}

export const Modal = ({
  title,
  onClose,
  children,
  footer,
  width = 820,
  style,
  headerStyle,
  contentStyle,
  onConfirm
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useScrollLock(true, modalRef);

  useEffect(() => {
    openModalCount++;
    if (openModalCount > 1) {
      console.error(
        `Multiple modals open simultaneously (${openModalCount}). Only one modal should be visible at a time.`
      );
    }
    return () => { openModalCount--; };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Enter' && onConfirm) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.stopPropagation();
        e.preventDefault();
        onConfirm();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, onConfirm]);

  return createPortal(
    <div ref={modalRef}>
      <LibModal width={String(width)} styles={{ modal: { ...surfaceBg, ...style } }}>
        <LibModalHeader
          modalHeaderTitle={title}
          onCloseModal={onClose}
          styles={{ root: { ...surfaceBg, ...headerStyle } }}
        />
        <LibModalContent styles={{ root: { padding: 0, gap: 'var(--spacing-sm)', ...contentStyle } }}>
          {children}
        </LibModalContent>
        {footer && <LibModalFooter styles={{ root: surfaceBg }}>{footer}</LibModalFooter>}
      </LibModal>
    </div>,
    document.body
  );
};
